import {
    Box,
    Button,
    ButtonGroup,
    Grid,
    GridItem,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useDisclosure
} from "@chakra-ui/react";
import React, { useState } from "react";
import { MdAdd, MdFileDownload } from "react-icons/md";
import { Link } from "react-router-dom";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Loader } from "../../components/Loader";
import { Container } from "../../components/Text/styles";
import { Title } from "../../components/Title";
import {
    ITierCardGenerateValues,
    TierCardGenerateForm,
    TierCardSearchForm
} from "../../forms";
import {
    AccountRole,
    AccountsDocument,
    ClientProfile,
    CodeStatsDocument,
    DoctorProfile,
    TierCodeStatus,
    TierCodeType,
    useAccountsQuery,
    useCodeLazyQuery,
    useCodeStatsQuery,
    useCodes2csvMutation,
    useDoctorSignupMutation,
    useGenerateTierCodesMutation,
    useSpecialistSignupMutation
} from "../../graphql";

const Cards: React.FC = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data, loading } = useCodeStatsQuery();
    const [codes2csv, { loading: generatingCSV }] = useCodes2csvMutation();
    const [generateTierCodes] = useGenerateTierCodesMutation();
    const [
        searchCode,
        { data: codeData, loading: loadingCode }
    ] = useCodeLazyQuery();

    const onDownload = async () => {
        const { data } = await codes2csv();

        const url = data.codes2csv;

        let a = document.createElement("a");
        a.href = url;
        a.download = url.split("/").pop();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleTierCardCodes = async (data: ITierCardGenerateValues) => {
        await generateTierCodes({
            variables: {
                tierIds: [parseInt(data.tierId)],
                type: TierCodeType.Card
            },
            refetchQueries: [
                {
                    query: CodeStatsDocument
                }
            ]
        });

        onClose();
    };

    return (
        <>
            <Modal
                closeOnOverlayClick={ false }
                isOpen={ isOpen }
                onClose={ onClose }
                size="xl"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Shto kode</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <TierCardGenerateForm
                            onSubmit={ (data) => handleTierCardCodes(data) }
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
            <>
                <Container>
                    <Grid templateColumns="repeat(6, 1fr)" gap={ 4 }>
                        <GridItem
                            colSpan={ 4 }
                            paddingRight={ 0 }
                            height="calc(100vh - 80px)"
                            overflow="scroll"
                        >
                            <Box
                                borderWidth="1px"
                                borderRadius="lg"
                                background="#FFF"
                                p="10px"
                            >
                                <Title title="Kodet e kartave të printueshme">
                                    <HStack spacing={ 3 }>
                                        <Button
                                            rightIcon={ <MdAdd /> }
                                            colorScheme="blue"
                                            size="sm"
                                            variant="ghost"
                                            onClick={ onOpen }
                                        >
                                            Shto kode
                                        </Button>

                                        <Button
                                            rightIcon={ <MdFileDownload /> }
                                            colorScheme="blue"
                                            size="sm"
                                            variant="ghost"
                                            width="100px"
                                            isLoading={ generatingCSV }
                                            onClick={ () => {
                                                onDownload();
                                            } }
                                        >
                                            Shkarko
                                        </Button>
                                    </HStack>
                                </Title>
                                { loading ? (
                                    <Loader />
                                ) : (
                                    <Table variant="striped" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Paketa</Th>
                                                <Th isNumeric>Të papërdorura</Th>
                                                <Th isNumeric>Të përdorura</Th>
                                                <Th isNumeric>Totali</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            { data?.codeStats.map((stat) => (
                                                <Tr key={ stat.tier.id }>
                                                    <Td>{ stat.tier.name }</Td>
                                                    <Td isNumeric>{ stat.unusedCount }</Td>
                                                    <Td isNumeric>{ stat.usedCount }</Td>
                                                    <Td isNumeric>{ stat.totalCount }</Td>
                                                </Tr>
                                            )) }
                                        </Tbody>
                                    </Table>
                                ) }
                            </Box>
                        </GridItem>
                        <GridItem colStart={ 5 } colEnd={ 7 }>
                            <Box
                                borderWidth="1px"
                                borderRadius="lg"
                                background="#FFF"
                                p="10px"
                            >
                                <Title title="Kontrollo një kod"></Title>

                                <TierCardSearchForm
                                    onReset={ () => {
                                        searchCode({
                                            variables: {
                                                code: ""
                                            }
                                        });
                                    } }
                                    onSubmit={ (data) => {
                                        searchCode({
                                            variables: {
                                                code: data.code
                                            }
                                        });
                                    } }
                                />

                                { loadingCode ? (
                                    <Loader />
                                ) : !codeData ? null : !codeData.code ? (
                                    "Asnjë rezultat"
                                ) : (
                                    <Table variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th isNumeric width="40%"></Th>
                                                <Th width="50%"></Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            <Tr>
                                                <Td isNumeric>Kodi</Td>
                                                <Td>{ codeData.code.code }</Td>
                                            </Tr>
                                            <Tr>
                                                <Td isNumeric>Statusi</Td>
                                                <Td>{ codeData.code.status }</Td>
                                            </Tr>
                                            <Tr>
                                                <Td isNumeric>Lloji i përdorimit të kodit</Td>
                                                <Td>{ codeData.code.type }</Td>
                                            </Tr>
                                            <Tr>
                                                <Td isNumeric>Paketa</Td>
                                                <Td>{ codeData.code.tier.name }</Td>
                                            </Tr>
                                            <Tr>
                                                <Td isNumeric>Data e lëshimit</Td>
                                                <Td>{ codeData.code.issueDate }</Td>
                                            </Tr>
                                            { codeData.code.status === TierCodeStatus.Used ? (
                                                <>
                                                    <Tr>
                                                        <Td isNumeric>Data e aktivizimit</Td>
                                                        <Td>{ codeData.code.claimDate }</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td isNumeric>Emri</Td>
                                                        <Td>
                                                            {
                                                                (codeData.code.client.profile as ClientProfile)
                                                                    .name
                                                            }
                                                        </Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td isNumeric>Mbiemri</Td>
                                                        <Td>
                                                            {
                                                                (codeData.code.client.profile as ClientProfile)
                                                                    .name
                                                            }
                                                        </Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td isNumeric>E-mail</Td>
                                                        <Td>{ codeData.code.client.email }</Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td isNumeric>E-mail</Td>
                                                        <Td>{ codeData.code.client.phoneNumber }</Td>
                                                    </Tr>
                                                </>
                                            ) : null }
                                        </Tbody>
                                    </Table>
                                ) }
                            </Box>
                        </GridItem>
                    </Grid>
                </Container>
            </>
        </>
    );
};

export default Cards;
